import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { X, FileText } from 'lucide-react';

/**
 * Renders a markdown string as React elements (headings, bold, lists, links, code).
 */
function MarkdownRenderer({ content }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<h3 key={key++} className="text-sm font-semibold text-gray-700 mt-3 mb-1">{parseInline(line.slice(4))}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={key++} className="text-base font-semibold text-gray-800 mt-4 mb-1 border-b pb-1">{parseInline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={key++} className="text-xl font-bold text-gray-900 mt-4 mb-2">{parseInline(line.slice(2))}</h1>);
    } else if (line.trim() === '---') {
      elements.push(<hr key={key++} className="my-4 border-gray-200" />);
    } else if (line.startsWith('- ')) {
      elements.push(
        <div key={key++} className="flex gap-1.5 text-sm text-gray-700 leading-relaxed">
          <span className="text-gray-400 mt-0.5 shrink-0">•</span>
          <span>{parseInline(line.slice(2))}</span>
        </div>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={key++} className="h-1" />);
    } else {
      elements.push(<p key={key++} className="text-sm text-gray-700 leading-relaxed">{parseInline(line)}</p>);
    }
  }

  return <div className="space-y-0.5">{elements}</div>;
}

function parseInline(text) {
  if (!text) return null;
  const parts = [];
  let remaining = String(text);
  let k = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`(.+?)`/);
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);
    const candidates = [boldMatch, codeMatch, linkMatch].filter(Boolean);

    if (candidates.length === 0) {
      parts.push(<span key={k++}>{remaining}</span>);
      break;
    }

    const earliest = candidates.reduce((a, b) => a.index <= b.index ? a : b);

    if (earliest.index > 0) {
      parts.push(<span key={k++}>{remaining.slice(0, earliest.index)}</span>);
    }

    if (earliest === boldMatch) {
      parts.push(<strong key={k++} className="font-semibold">{earliest[1]}</strong>);
    } else if (earliest === codeMatch) {
      parts.push(<code key={k++} className="bg-gray-100 px-1 rounded text-xs font-mono">{earliest[1]}</code>);
    } else if (earliest === linkMatch) {
      parts.push(
        <a key={k++} href={earliest[2]} target="_blank" rel="noopener noreferrer"
           className="text-blue-600 underline hover:text-blue-800">
          {earliest[1]}
        </a>
      );
    }

    remaining = remaining.slice(earliest.index + earliest[0].length);
  }

  return <>{parts}</>;
}

export function ResearchReportModal({ open, onClose, content, companyName }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Intelligence Report — {companyName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {content ? (
            <MarkdownRenderer content={content} />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No report available yet. Run research to generate one.</p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="w-4 h-4 mr-1" />Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
